import { useProductsCollection } from '@/queries';
import ProductCard from '@features/shop/components/product-card';
import { Breakpoints } from '@settings';
import { ProductTypeId } from '@types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import CompactProductCard from '../CompactProductCard';
import Slider, { Settings as SliderSettings } from 'react-slick';
import classNames from 'classnames';
import ProductImageSliderBullets from '@components/tailwind/ProductImageSliderBullets';
import { sliderGoTo } from '@features/customization/utils';

const MAX_NUMBER_OF_CARDS = 3;
const SLIDER_OPTIONS: Partial<SliderSettings> = {
  slidesToShow: MAX_NUMBER_OF_CARDS,
  slidesToScroll: 1,
  arrows: false,
  variableWidth: false,
  infinite: false,
  centerMode: false,
  dots: false,
  responsive: [
    {
      breakpoint: Breakpoints.MD,
      settings: {
        slidesToShow: 1,
        centerMode: true,
      },
    },
  ],
};

export const ULTRA_LIGHT_ITEMS_FEATURES = {
  [ProductTypeId.ULTRA_LIGHT_22_QT]: ['Holds 30 cans', 'Empty weight 11.5 lbs', '19 ½ x 15 ¾ x 13'],
  [ProductTypeId.ULTRA_LIGHT_32_QT]: [
    'Holds 48 cans | 30 lbs Ice',
    'Empty weight 13 lbs',
    '23 x 14 ½ x 14 ½',
  ],
  [ProductTypeId.ULTRA_LIGHT_COOLER_TYPE]: [
    'Holds 76 cans | 51 lbs Ice',
    'Empty weight 21 lbs',
    '27 ¼ x 17 ¼ x 17',
  ],
  [ProductTypeId.ULTRA_LIGHT_52_QT_WHEELED]: [
    'Holds 78 cans | 53 lbs Ice',
    'Empty weight 30 lbs',
    '29 x 18 ¾ x 18 ¼',
  ],
  [ProductTypeId.ULTRA_LIGHT_72_QT_WHEELED]: [
    'Holds 96 Cans | 71 lbs Ice',
    'Empty weight 35 lbs',
    '36 ¾ x 18 ¼ x 18 ¾',
  ],
  0: [],
};

const sortOrder = [
  ProductTypeId.ULTRA_LIGHT_52_QT_WHEELED,
  ProductTypeId.ULTRA_LIGHT_COOLER_TYPE,
  ProductTypeId.ULTRA_LIGHT_32_QT,
  ProductTypeId.ULTRA_LIGHT_22_QT,
];

const UltraLightCollection = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const isMobile = useMediaQuery({ maxWidth: Breakpoints.MD });
  const { data, refetch: getCollection } = useProductsCollection({
    'tags[]': ['ultra-light'],
  });

  useEffect(() => {
    getCollection();
  }, []);

  const sortedData = useMemo(
    () =>
      (data?.items || [])
        .filter(
          (item) =>
            item.categoryName === 'Hard Coolers' &&
            Boolean(
              ULTRA_LIGHT_ITEMS_FEATURES[item?.item?.attributes?.all?.find((attr) => attr.key === 'type')?.id]
            )
        )
        .sort((a, b) => {
          return (
            sortOrder.indexOf(a?.item?.attributes?.all?.find((attr) => attr.key === 'type')?.id) -
            sortOrder.indexOf(b?.item?.attributes?.all?.find((attr) => attr.key === 'type')?.id)
          );
        }),
    [data]
  );

  const sliderRef = useRef<Slider>(null);

  if (!data?.items || data.items.length === 0) return null;

  const numSlides = data.items.length;
  const numPages = numSlides - MAX_NUMBER_OF_CARDS;
  const renderCarousel = isMobile || numSlides > 3;

  return (
    <div
      className={classNames({
        'tw-bg-white tw-py-5': isMobile,
      })}>
      {!renderCarousel && (
        <div className='tw-mx-auto tw-grid tw-w-full tw-max-w-[1440px] tw-grid-cols-3 tw-justify-center tw-gap-4'>
          {sortedData.map((item, index) => (
            <ProductCard
              key={item.id}
              displayPosition={index + 1}
              item={item}
              features={
                ULTRA_LIGHT_ITEMS_FEATURES[
                  item?.item?.attributes?.all?.find((attr) => attr.key === 'type')?.id
                ]
              }
            />
          ))}
        </div>
      )}

      {renderCarousel && (
        <div className='tw-mx-auto tw-max-w-[1440px]'>
          <Slider ref={sliderRef} {...SLIDER_OPTIONS} beforeChange={(_, next) => setActiveSlide(next)}>
            {sortedData.map((item) => (
              <div className='tw-px-2 tw-pb-3' key={item.id}>
                <CompactProductCard
                  item={item}
                  features={
                    ULTRA_LIGHT_ITEMS_FEATURES[
                      item?.item?.attributes?.all?.find((attr) => attr.key === 'type')?.id
                    ]
                  }
                  buttonVariant='tertiary'
                  buttonText='Learn more'
                />
              </div>
            ))}
          </Slider>

          <ProductImageSliderBullets
            count={isMobile ? numSlides : numPages}
            onClick={sliderGoTo(sliderRef)}
            activeSlide={activeSlide}
          />
        </div>
      )}
    </div>
  );
};

export default UltraLightCollection;
